import { useCallback, useContext, useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { buildAdminUserNavigationState, buildDepartmentDetailPath, buildDepartmentUserDetailPath, buildDepartmentUserManagePath, PATH_INFO, resolveAdminUserBackToPath, resolveAdminUserExplicitBackToPath, shouldStopForInvalidContextFetchResult, shouldUseAdminUserHistoryBack } from "../../../../util/helper"
import { Button, Col, Divider, Flex, List, Row, Spin } from "antd"
import { CaretRightOutlined, EditOutlined, LeftOutlined, PauseOutlined, ProfileOutlined } from "@ant-design/icons"
import { UpdateUsersContext, UsersContext } from "../../../../../context/userContext"
import { CompanyContext, UpdateCompanyContext } from "../../../../../context/companyContext"
import { DepartmentContext, UpdateDepartmentContext } from "../../../../../context/departmentContext"
import { clientFetch } from "../../../../../utils/clientFetch"

export function DepartmentUserDetail() {
  const { user } = useContext(UsersContext)
  const { refreshUser, toggleUserDisabled } = useContext(UpdateUsersContext)
  const { company } = useContext(CompanyContext)
  const { refreshCompany } = useContext(UpdateCompanyContext)
  const { departmentDetail } = useContext(DepartmentContext)
  const { refreshDepartmentDetail } = useContext(UpdateDepartmentContext)
  const { companyId, departmentId, userId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const trimmedUserId = userId?.trim() ?? ""
  const backTo = resolveAdminUserBackToPath(location.state, companyId, departmentId)
  const explicitBackTo = resolveAdminUserExplicitBackToPath(location.state)
  const useHistoryBack = shouldUseAdminUserHistoryBack(location.state)
  const currentDetailPath = buildDepartmentUserDetailPath(companyId, departmentId, trimmedUserId)

  const [userState, setUserState] = useState(user.is_disabled)
  const [sampleStats, setSampleStats] = useState<{ clothType: string; count: number }[]>([])
  const [sampleStatsTotal, setSampleStatsTotal] = useState<number | null>(null)
  const [sampleStatsLoading, setSampleStatsLoading] = useState(false)
  const [isPageReady, setIsPageReady] = useState(false)

  useEffect(() => {
    let isCancelled = false

    const validateAndLoadDetail = async () => {
      setIsPageReady(false)

      if (!trimmedUserId || !companyId || !departmentId) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
        return
      }

      try {
        const [userResult, companyResult, departmentResult] = await Promise.all([
          refreshUser(trimmedUserId),
          refreshCompany(companyId),
          refreshDepartmentDetail(departmentId),
        ])

        if (isCancelled) {
          return
        }

        if (
          shouldStopForInvalidContextFetchResult(userResult, navigate) ||
          shouldStopForInvalidContextFetchResult(companyResult, navigate) ||
          shouldStopForInvalidContextFetchResult(departmentResult, navigate)
        ) {
          return
        }

        const userData = userResult.data
        const departmentData = departmentResult.data

        if (!userData || !departmentData) {
          navigate(PATH_INFO.ERROR.PATH, { replace: true })
          return
        }

        const targetCompanyId = Number(companyId)
        const targetDepartmentId = Number(departmentId)
        const departmentCompanyId = departmentData.company?.id
        const userDepartmentId = userData.department?.id

        if (departmentCompanyId !== targetCompanyId) {
          navigate(PATH_INFO.ERROR.PATH, { replace: true })
          return
        }

        if (userDepartmentId !== targetDepartmentId) {
          if (typeof userDepartmentId === 'number' && userDepartmentId > 0) {
            const destination = buildDepartmentUserDetailPath(companyId, `${userDepartmentId}`, trimmedUserId)
            const correctedBackTo = explicitBackTo
              ?? buildDepartmentDetailPath(companyId, `${userDepartmentId}`)
              ?? backTo
            if (!destination) {
              navigate(PATH_INFO.ERROR.PATH, { replace: true })
              return
            }
            navigate(destination, { replace: true, state: buildAdminUserNavigationState(correctedBackTo, undefined, useHistoryBack) })
            return
          }

          navigate(PATH_INFO.ERROR.PATH, { replace: true })
          return
        }

        if (isCancelled) {
          return
        }

        setIsPageReady(true)
      } catch (error) {
        console.error("Error validating department user detail.", error)
        if (isCancelled) {
          return
        }
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
      }
    }

    validateAndLoadDetail()

    return () => {
      isCancelled = true
    }
  }, [refreshUser, refreshCompany, refreshDepartmentDetail, trimmedUserId, companyId, departmentId, navigate, backTo, explicitBackTo, useHistoryBack])

  useEffect(() => {
    setUserState(user.is_disabled)
  }, [user])

  const fetchSampleStats = useCallback(async () => {
    if (!trimmedUserId || !isPageReady) return
    setSampleStatsLoading(true)
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users/${trimmedUserId}/sample-stats`)

      if (res.status === 404) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
        return
      }

      if (!res.ok) {
        throw new Error("Failed to fetch sample stats")
      }
      const payload = await res.json()
      setSampleStats(Array.isArray(payload?.items) ? payload.items : [])
      setSampleStatsTotal(typeof payload?.total === "number" ? payload.total : null)
    } catch (error) {
      console.error("Error fetching sample stats", error)
      setSampleStats([])
      setSampleStatsTotal(null)
    } finally {
      setSampleStatsLoading(false)
    }
  }, [isPageReady, navigate, trimmedUserId])

  useEffect(() => {
    if (!isPageReady) return
    fetchSampleStats()
  }, [fetchSampleStats, isPageReady])

  const onClickUserManage = useCallback(() => {
    if (!companyId || !departmentId || !trimmedUserId) return

    const destination = buildDepartmentUserManagePath(companyId, departmentId, trimmedUserId)
    if (!destination) return

    navigate(destination, {
      replace: true,
      state: buildAdminUserNavigationState(backTo, currentDetailPath, useHistoryBack)
    })
  }, [companyId, departmentId, trimmedUserId, navigate, backTo, currentDetailPath, useHistoryBack])

  const dateformat = useCallback((date: string) => {
    if (!date) return
    const result = new Date(date).toISOString().split('T')[0]
    return result
  }, [])

  const userInfoTitle = [
    { key: 'post', label: '役職', value: user.position },
    { key: 'email', label: 'メールアドレス', value: user.email },
    { key: 'pin', label: 'PIN', value: user.pin_code },
    { key: 'end_at', label: '終了予定日', value: company?.contract_end_date },
    { key: 'created_at', label: '登録日', value: dateformat(user.created_at) },
    { key: 'start_at', label: '利用開始日', value: dateformat(user.start_date) },
    { key: 'last_login_at', label: '最終ログイン日', value: dateformat(user.last_login_at) }
  ]

  const onClickToggleStatus = useCallback(async () => {
    if (!trimmedUserId) return
    try {
      await toggleUserDisabled(trimmedUserId)
      setUserState((prev) => !prev)
    } catch {
      return
    }
  }, [toggleUserDisabled, trimmedUserId])

  if (!isPageReady) {
    return (
      <Flex justify="center" align="center" className="min-h-[50vh]">
        <Spin size="large" tip="読み込み中..." />
      </Flex>
    )
  }

  return (
    <div>
      <Button
        variant="text"
        color="primary"
        icon={<LeftOutlined />}
        onClick={() => {
          if (useHistoryBack) {
            navigate(-1)
            return
          }
          if (backTo) {
            navigate(backTo, { replace: true })
            return
          }
          navigate(-1)
        }}
        className="mb-2 -ml-3"
      >
        戻る
      </Button>
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">ユーザ詳細画面</p>
        <Button variant="solid" color="primary" icon={<EditOutlined />} onClick={onClickUserManage}>編集</Button>
        <Button variant="outlined" color={userState ? "green" : "danger"} icon={userState ? <CaretRightOutlined /> : <PauseOutlined />} onClick={onClickToggleStatus}>{userState ? '復活' : '停止'}</Button>
      </div>
      <Divider />
      <div className="flex p-2 text-xl mb-3">
        <ProfileOutlined className="text-[#1677ff]" />
        <span className="pl-2 break-all">{`${company?.company_name} / ${departmentDetail.name} / ${user.name}`}</span>
      </div>
      <Flex>
        <Row justify={'start'} align={'top'} className="w-full h-fit" gutter={[24, 24]}>
          {userInfoTitle.map((section, index) => (
            <Col xs={24} xl={12} xxl={8} key={index}>
              <List
                header={<div className="font-bold">{section.label}</div>}
                bordered
                dataSource={[section]}
                renderItem={(item) => (
                  <List.Item style={{ background: '#FFFFFF' }}>
                    <p className={`m-0 pl-2 ${item.value ? '' : 'text-black/25'}`}>{item.value || 'データなし'}</p>
                  </List.Item>)}
                className="overflow-hidden bg-[#FAFAFA]"
              />
            </Col>
          )
          )}
        </Row>
        <div className="w-full max-w-[380px] ml-6" >
          <div className="h-[47px] px-5 py-2 border border-[#d9d9d9] border-solid border-b-0 flex items-center rounded-t-lg bg-[#fafafa] font-semibold" >
            ユーザ採寸型数
          </div>
          <List
            bordered
            loading={sampleStatsLoading}
            dataSource={sampleStats}
            locale={{ emptyText: "データなし" }}
            renderItem={(item) => (
              <List.Item style={{ background: '#FFFFFF' }} >
                <div className="flex w-full justify-between">
                  <span>{item.clothType}</span>
                  <span>{`${item.count}型`}</span>
                </div>
              </List.Item>
            )}
            className="bg-[#FAFAFA] w-full rounded-none max-h-[400px] overflow-y-auto"
          />
          <div className="h-[47px] px-5 py-2 border border-[#d9d9d9] border-solid border-t-0 flex items-center rounded-b-lg bg-[#fafafa] font-semibold">
            <div className="flex w-full justify-between font-semibold">
              <span>合計</span>
              <span>{`${sampleStatsTotal || 0}型`}</span>
            </div>
          </div>
        </div>
      </Flex>
    </div>
  )
}
